/**
 * useEvent — Tauri 事件监听统一封装
 * 监听 Rust → 前端的事件推送（domain:entity:action 格式）
 *
 * 双导出模式：
 * - useEvent<T>(event, handler) — 组件内使用，自动注册 + onUnmounted 清理
 * - listenEvent<T>(event, handler) — 手动管理生命周期，返回 UnlistenFn
 */
import { onMounted, onUnmounted } from 'vue'
import { platformListen, type PlatformUnlisten } from '@/platform/bridge'
import type { TauriEvent, TauriEventPayloadMap } from '@/types/ipc'

const noopUnlisten: PlatformUnlisten = () => {}

// ─── 手动管理生命周期版本 ────────────────────────────────
export async function listenEvent<E extends TauriEvent>(
  event: E,
  handler: (payload: TauriEventPayloadMap[E]) => void,
): Promise<PlatformUnlisten> {
  return platformListen<TauriEventPayloadMap[E]>(event, handler)
}

// ─── Composable 版本（组件内使用，自动清理） ────────────
export function useEvent<E extends TauriEvent>(
  event: E,
  handler: (payload: TauriEventPayloadMap[E]) => void,
) {
  let unlisten: PlatformUnlisten | null = null

  onMounted(async () => {
    try {
      unlisten = await listenEvent(event, handler)
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn(`[useEvent] listen ${event} failed`, e)
      }
      unlisten = noopUnlisten
    }
  })

  onUnmounted(() => {
    unlisten?.()
    unlisten = null
  })

  return {
    unlisten: () => {
      unlisten?.()
      unlisten = null
    },
  }
}
